import { Side } from '@openeducation/oe-it-shared-types';
import {
  secondaryFontFamily,
  styled,
  useDimension,
} from '@openeducation/oe-it-themes';
import { rgba } from 'polished';
import React, { useEffect } from 'react';
import {
  Modal as DefaultModal,
  SafeAreaView,
  StyleProp,
  ViewStyle,
} from 'react-native';

import { IconButton } from '../icon-button/icon-button';
import { OEText } from '../oe-text/oe-text';

type SuccessMessageType = {
  message: string;
  backgroundColor?: string;
  color?: string;
};

export interface OEModalProps {
  visible: boolean;
  showCloseButton?: boolean;
  showBackButton?: boolean;
  fullscreenMobile?: boolean;
  style?: StyleProp<ViewStyle>;
  onClose?: () => void;
  successMessage?: SuccessMessageType;
  successMessageSecondary?: Omit<SuccessMessageType, 'backgroundColor'>;
  autoDismiss?: boolean;
  children?: React.ReactNode;
}

const AUTO_DISMISS_TIMEOUT = 3000;

export const OEModal: React.FC<OEModalProps> = ({
  visible,
  children,
  showCloseButton,
  showBackButton,
  fullscreenMobile = true,
  style,
  onClose,
  successMessage,
  autoDismiss,
  successMessageSecondary,
}) => {
  const { isMobile } = useDimension();
  const isSuccessModal = !!successMessage?.message;

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (visible && autoDismiss) {
      timeout = setTimeout(() => {
        onClose?.();
      }, AUTO_DISMISS_TIMEOUT);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [visible, autoDismiss, onClose]);

  return (
    <DefaultModal
      transparent
      visible={visible}
      presentationStyle="overFullScreen"
      animationType="fade"
    >
      <Background>
        <ModalContent
          style={style}
          isSuccessModal={isSuccessModal}
          fullscreenMobile={isMobile && fullscreenMobile}
          successBackgroundColor={successMessage?.backgroundColor}
        >
          {showCloseButton && (
            <CloseButton name="times" onPress={onClose} side="right" />
          )}
          {showBackButton && (
            <CloseButton name="arrow-left" onPress={onClose} side="left" />
          )}
          {isSuccessModal && (
            <SuccessMessageWrapper>
              <SuccessMessage color={successMessage?.color}>
                {successMessage?.message}
              </SuccessMessage>
              {!!successMessageSecondary?.message && (
                <SuccessMessageSecondary color={successMessageSecondary?.color}>
                  {successMessageSecondary?.message}
                </SuccessMessageSecondary>
              )}
            </SuccessMessageWrapper>
          )}
          {children}
        </ModalContent>
      </Background>
    </DefaultModal>
  );
};

const Background = styled(SafeAreaView)`
  flex: 1;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => rgba(theme.color.dark1, 0.8)};
`;

interface ModalContentProps {
  fullscreenMobile: boolean;
  isSuccessModal?: boolean;
  successBackgroundColor?: string;
}

const ModalContent = styled.View<ModalContentProps>`
  max-width: ${({ isSuccessModal }) => (isSuccessModal ? 360 : 800)}px;
  width: ${({ fullscreenMobile }) => (fullscreenMobile ? '100%' : '95%')};
  border-radius: ${({ fullscreenMobile }) => (fullscreenMobile ? 0 : 8)}px;
  height: ${({ fullscreenMobile }) => (fullscreenMobile ? '100%' : 'auto')};
  box-shadow: 0px 0px 16px
    ${({ theme, fullscreenMobile }) =>
      rgba(theme.color.dark1, fullscreenMobile ? 0 : 0.1)};
  background-color: ${({ theme, isSuccessModal, successBackgroundColor }) =>
    isSuccessModal
      ? successBackgroundColor || theme.color.primary
      : theme.color.white};
`;

const CloseButton = styled(IconButton).attrs<{
  isSuccessModal: boolean;
}>(({ theme, isSuccessModal }) => ({
  color: isSuccessModal ? theme.color.white : theme.color.dark1,
  size: 16,
}))<{ side: Side }>`
  position: absolute;
  padding: 8px;
  top: 10px;
  z-index: 9;
  ${({ side }) => side === 'right' && 'right: 20px;'};
  ${({ side }) => side === 'left' && 'left: 10px;'};
`;

const SuccessMessage = styled(OEText).attrs(({ theme, color }) => ({
  color: color || theme.color.white,
  fontSize: 20,
  fontFamily: secondaryFontFamily,
  fontType: 'medium',
}))``;

const SuccessMessageSecondary = styled(OEText).attrs(({ theme, color }) => ({
  color: color || theme.color.white,
  fontSize: 16,
  fontFamily: secondaryFontFamily,
}))`
  margin-top: 18px;
`;

const SuccessMessageWrapper = styled.View`
  align-items: center;
  justify-content: center;
  padding: 60px;
`;
